/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";
import {
  LogFilter,
  LogFilter$Outbound,
  LogFilter$outboundSchema,
} from "./logfilter.js";
import {
  ToolUsageUserFilter,
  ToolUsageUserFilter$Outbound,
  ToolUsageUserFilter$outboundSchema,
} from "./toolusageuserfilter.js";

/**
 * Sort order
 */
export const ListToolUsageTracesPayloadSort = {
  Asc: "asc",
  Desc: "desc",
} as const;
/**
 * Sort order
 */
export type ListToolUsageTracesPayloadSort = ClosedEnum<
  typeof ListToolUsageTracesPayloadSort
>;

/**
 * Tool usage trace outcome
 */
export const ListToolUsageTracesPayloadStatuses = {
  Error: "error",
  Success: "success",
  Blocked: "blocked",
  Pending: "pending",
} as const;
/**
 * Tool usage trace outcome
 */
export type ListToolUsageTracesPayloadStatuses = ClosedEnum<
  typeof ListToolUsageTracesPayloadStatuses
>;

/**
 * Tool usage target type
 */
export const ListToolUsageTracesPayloadTargetTypes = {
  HostedMcpServer: "hosted_mcp_server",
  TunneledMcpServer: "tunneled_mcp_server",
  MetaMcpServer: "meta_mcp_server",
  ShadowMcpServer: "shadow_mcp_server",
  LocalTool: "local_tool",
  Skill: "skill",
} as const;
/**
 * Tool usage target type
 */
export type ListToolUsageTracesPayloadTargetTypes = ClosedEnum<
  typeof ListToolUsageTracesPayloadTargetTypes
>;

/**
 * Payload for listing target-aware MCP and tool usage traces
 */
export type ListToolUsageTracesPayload = {
  /**
   * Optional account type filter ('team' or 'personal'). 'team' includes unclassified traces.
   */
  accountType?: string | undefined;
  /**
   * Agent identities to include. Combined with user_filters by AND, so an agent and a person select the calls that agent made on that person's behalf.
   */
  agentIds?: Array<string> | undefined;
  /**
   * MCP client keys (lowercased self-reported client names; 'unattributed' selects calls Speakeasy never saw an initialize handshake for) to include
   */
  clientKeys?: Array<string> | undefined;
  /**
   * Cursor for pagination
   */
  cursor?: string | undefined;
  /**
   * Arbitrary attribute filter conditions from the af URL param
   */
  filters?: Array<LogFilter> | undefined;
  /**
   * Start time in ISO 8601 format
   */
  from: Date;
  /**
   * Hook plugin sources to include. Direct hosted MCP calls have no hook source and are excluded when this filter is set.
   */
  hookSources?: Array<string> | undefined;
  /**
   * Deprecated: use mcp_server_ids. Hosted MCP toolset slugs to include
   */
  hostedToolsetSlugs?: Array<string> | undefined;
  /**
   * Number of traces to return
   */
  limit?: number | undefined;
  /**
   * MCP server ids to include. Matches calls served by the server, including calls recorded before they carried a server id.
   */
  mcpServerIds?: Array<string> | undefined;
  /**
   * Gateway (meta MCP server) ids to include: calls dispatched through the gateway to its members plus calls observed against the gateway itself
   */
  metaMcpServerIds?: Array<string> | undefined;
  /**
   * Free-text attribute search string from the q URL param. Matches useful identifier attributes such as Speakeasy URN, conversation ID, and trigger instance ID.
   */
  query?: string | undefined;
  /**
   * Shadow MCP server names to include
   */
  shadowServerNames?: Array<string> | undefined;
  /**
   * Sort order
   */
  sort?: ListToolUsageTracesPayloadSort | undefined;
  /**
   * Trace outcomes to include (error, success, blocked, pending). Empty means all.
   */
  statuses?: Array<ListToolUsageTracesPayloadStatuses> | undefined;
  /**
   * Target types to include. Empty means all target types.
   */
  targetTypes?: Array<ListToolUsageTracesPayloadTargetTypes> | undefined;
  /**
   * End time in ISO 8601 format
   */
  to: Date;
  /**
   * Typed user identities to include. An email also matches the calls an agent made on that person's behalf.
   */
  userFilters?: Array<ToolUsageUserFilter> | undefined;
};

/** @internal */
export const ListToolUsageTracesPayloadSort$outboundSchema: z.ZodMiniEnum<
  typeof ListToolUsageTracesPayloadSort
> = z.enum(ListToolUsageTracesPayloadSort);

/** @internal */
export const ListToolUsageTracesPayloadStatuses$outboundSchema: z.ZodMiniEnum<
  typeof ListToolUsageTracesPayloadStatuses
> = z.enum(ListToolUsageTracesPayloadStatuses);

/** @internal */
export const ListToolUsageTracesPayloadTargetTypes$outboundSchema:
  z.ZodMiniEnum<typeof ListToolUsageTracesPayloadTargetTypes> = z.enum(
    ListToolUsageTracesPayloadTargetTypes,
  );

/** @internal */
export type ListToolUsageTracesPayload$Outbound = {
  account_type?: string | undefined;
  agent_ids?: Array<string> | undefined;
  client_keys?: Array<string> | undefined;
  cursor?: string | undefined;
  filters?: Array<LogFilter$Outbound> | undefined;
  from: string;
  hook_sources?: Array<string> | undefined;
  hosted_toolset_slugs?: Array<string> | undefined;
  limit: number;
  mcp_server_ids?: Array<string> | undefined;
  meta_mcp_server_ids?: Array<string> | undefined;
  query?: string | undefined;
  shadow_server_names?: Array<string> | undefined;
  sort: string;
  statuses?: Array<string> | undefined;
  target_types?: Array<string> | undefined;
  to: string;
  user_filters?: Array<ToolUsageUserFilter$Outbound> | undefined;
};

/** @internal */
export const ListToolUsageTracesPayload$outboundSchema: z.ZodMiniType<
  ListToolUsageTracesPayload$Outbound,
  ListToolUsageTracesPayload
> = z.pipe(
  z.object({
    accountType: z.optional(z.string()),
    agentIds: z.optional(z.array(z.string())),
    clientKeys: z.optional(z.array(z.string())),
    cursor: z.optional(z.string()),
    filters: z.optional(z.array(LogFilter$outboundSchema)),
    from: z.pipe(z.date(), z.transform(v => v.toISOString())),
    hookSources: z.optional(z.array(z.string())),
    hostedToolsetSlugs: z.optional(z.array(z.string())),
    limit: z._default(z.int(), 100),
    mcpServerIds: z.optional(z.array(z.string())),
    metaMcpServerIds: z.optional(z.array(z.string())),
    query: z.optional(z.string()),
    shadowServerNames: z.optional(z.array(z.string())),
    sort: z._default(ListToolUsageTracesPayloadSort$outboundSchema, "desc"),
    statuses: z.optional(
      z.array(ListToolUsageTracesPayloadStatuses$outboundSchema),
    ),
    targetTypes: z.optional(
      z.array(ListToolUsageTracesPayloadTargetTypes$outboundSchema),
    ),
    to: z.pipe(z.date(), z.transform(v => v.toISOString())),
    userFilters: z.optional(z.array(ToolUsageUserFilter$outboundSchema)),
  }),
  z.transform((v) => {
    return remap$(v, {
      accountType: "account_type",
      agentIds: "agent_ids",
      clientKeys: "client_keys",
      hookSources: "hook_sources",
      hostedToolsetSlugs: "hosted_toolset_slugs",
      mcpServerIds: "mcp_server_ids",
      metaMcpServerIds: "meta_mcp_server_ids",
      shadowServerNames: "shadow_server_names",
      targetTypes: "target_types",
      userFilters: "user_filters",
    });
  }),
);

export function listToolUsageTracesPayloadToJSON(
  listToolUsageTracesPayload: ListToolUsageTracesPayload,
): string {
  return JSON.stringify(
    ListToolUsageTracesPayload$outboundSchema.parse(listToolUsageTracesPayload),
  );
}
